// W4. Server-derived placement/detach legality. The server derives
// `state.boardLegality` (banned site cells / spawn extensions / locked
// equipment ids) after every commit and projects it verbatim; the fold stores
// it on the host and every auth consumption site reads it FROM the mirror
// instead of re-running the local aura derivation. Flag-off (legacy) keeps
// the local pure-module computation.

import { bannedSitePlacementCells } from '../../../utils/game/engine/automation/ops/auraSiteRestriction';
import {
  auraExtendedSpawnCells,
  spawnExtensionCellsFromLegality,
} from '../../../utils/game/engine/automation/ops/auraSpawnExtension';
import { lockedEquipmentIds } from '../../../utils/game/engine/automation/ops/auraArtifactRestrictions';

// Fold: adopt the projected bag by reference (Immer structural sharing makes
// identity a valid change check). A mid-drag legality change (an aura landing
// or leaving while a card is held) refreshes the placement halos, which are
// otherwise computed once at drag start.
export function applyBoardLegalityFromMirror(host, mirrorState) {
  const bag = mirrorState?.boardLegality;
  if (!bag || host.boardLegality === bag) return;
  host.boardLegality = bag;
  const drag = host.dragging;
  if (drag?.members) {
    drag.reach = host._computeDragReach(drag.members, drag.freshPlayIds);
    host.forceUpdate?.();
  }
}

// Cells where sites can't be played (Salt the Earth). Auth: server truth from
// the mirror; sandbox: local derivation over live meshes.
export function bannedSiteCellsFor(host) {
  if (host._authZonesEnabled?.()) {
    return new Set(host.boardLegality?.bannedSiteCells || []);
  }
  return bannedSitePlacementCells(host._meshesExcludingStagedDrag(), host._getFullCard);
}

// Extra legal summon cells for `card` granted by aura spawn extensions
// (Evil Presence / Crusade / …). Auth: match the candidate against the
// server-derived per-source records; sandbox: local mesh walk.
export function extendedSpawnCellsFor(host, card, controller) {
  const fullCard = host._getFullCard(card.cardId);
  if (host._authZonesEnabled?.()) {
    return spawnExtensionCellsFromLegality(
      host.boardLegality?.spawnExtensions, card, fullCard, controller,
    );
  }
  return auraExtendedSpawnCells(
    card, fullCard, host.meshes, host._cellAtWorld, host._getFullCard, controller,
  );
}

// Equipped-artifact ids that can't be detached (Cursed Iron). Auth: server
// truth from the mirror; sandbox: local derivation.
export function lockedEquipmentIdSet(host) {
  if (host._authZonesEnabled?.()) {
    return new Set(host.boardLegality?.lockedEquipmentIds || []);
  }
  return lockedEquipmentIds(host.meshes, host._cellAtWorld, host._getFullCard);
}
